import React, { useContext, useEffect, useState } from 'react';
import { Loader, useToast } from '@pw-tech/omni-ui';
import ValidateCourseModal from '../RegisterForm/ValidateCourseModal/ValidateCourseModal';
import { useGetRegistrationDetails, useUpdateRegistrationDetails } from '../../hooks/useRegistrationDetails';
import { useRouter } from 'next/router';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { useGetAllCourses } from '@/modules/RegisterLayout/hooks/useGetAllCourses';
import CourseSelection from '@/modules/CourseSelection/components/CourseSelection/CourseSelection';
import { useLeadCapture } from '@/modules/RegisterLayout/hooks/useLeadCapture';
import EligibleForAdmissionModal from '@/modules/RegisterLayout/Modals/EligibleForAdmissionModal/EligibleForAdmissionModal';
import TrackShiftModal from '@/modules/RegisterLayout/Modals/TrackShiftModal/TrackShiftModal';
import useTrackShift from '@/modules/RegisterLayout/hooks/useTrackShift';
const InitializeComponent = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [isInitialized, setIsInitialized] = useState(true);
  const [showValidateCourseModal, setShowValidateCourseModal] = useState(false);
  const [showCourseSelectionModal, setShowCourseSelectionModal] = useState(false);
  const [showEligibleAdmissionModal, setShowEligibleAdmissionModal] = useState(false);
  const [showTrackShiftModal, setShowTrackShiftModal] = useState(false);
  const [dataLoadingComplete, setDataLoadingComplete] = useState(false);
  const {
    data: userRegistrationDetails,
    isFetching: isFetchingRegistrationDetails,
    refetch: refetchRegistrationDetails
  } = useGetRegistrationDetails({});
  const {
    data: courseData,
    isLoading: isLoadingCourses
  } = useGetAllCourses({
    enabled: true
  });
  const {
    mutate: updateRegistrationDetails,
    isLoading: isUpdatingRegistrationDetails
  } = useUpdateRegistrationDetails();
  const {
    registrationDetails,
    setRegistrationDetails
  } = useContext(RegistrationContext);
  const {
    courses,
    setCourses,
    selectedCourseData,
    setSelectedCourseData
  } = useContext(CourseContext);
  const {
    mutateAsync: asyncLeadCapture
  } = useLeadCapture();
  const {
    mutate: trackShift,
    isLoading: isTrackShiftLoading
  } = useTrackShift();
  const {
    toast: showToaster
  } = useToast();
  const router = useRouter();
  const {
    courseId,
    step,
    coming_from_page,
    section,
    coming_from
  } = router.query;

  // Step 1: Initialize courses in context
  useEffect(() => {
    if (courseData?.length && !courses?.length) {
      setCourses(courseData);
    }
  }, [courseData, courses?.length, setCourses]);

  // Step 2: Initialize registration details in context
  useEffect(() => {
    if (userRegistrationDetails?._id) {
      setRegistrationDetails(userRegistrationDetails);
    }
  }, [userRegistrationDetails]);

  // Step 2.5: Check if user needs track shift (DA rejected, needs to move to CEE flow)
  useEffect(() => {
    const shouldShowTrackShiftModal = registrationDetails?.isEligibleForDirectAdmission === true && registrationDetails?.isTrackShiftAcknowledged === false;
    setShowTrackShiftModal(shouldShowTrackShiftModal);
  }, [registrationDetails?.isEligibleForDirectAdmission, registrationDetails?.isTrackShiftAcknowledged]);

  // Step 3: Set selected course based on courseId from URL or registration details
  useEffect(() => {
    if (courses?.length) {
      const currentCourseData = courses.find((course: any) => registrationDetails && course._id === registrationDetails.courseId);
      setSelectedCourseData(currentCourseData);
    }
  }, [courses, registrationDetails?.courseId]);

  // Step 4: Mark data loading as complete when all essential data is loaded
  useEffect(() => {
    const isRegistrationLoaded = !isFetchingRegistrationDetails;
    const isCoursesLoaded = !isLoadingCourses && courses?.length > 0;
    if (isRegistrationLoaded && isCoursesLoaded && !dataLoadingComplete) {
      setDataLoadingComplete(true);
    }
  }, [isFetchingRegistrationDetails, isLoadingCourses, courses?.length, dataLoadingComplete]);
  const submitRegisterForm = (data: any) => {
    updateRegistrationDetails({
      courseId: data._id,
      batchId: data.batch._id,
      step: 1,
      campusId: null,
      campusId2: null
    }, {
      onSuccess: (response: any) => {
        if (response?.data) {
          setRegistrationDetails(response.data);
          setShowValidateCourseModal(false);
          setIsInitialized(false);
          setDataLoadingComplete(true);
          asyncLeadCapture();
          router.push({
            pathname: '/register',
            query: {
              courseId: response?.data?.courseId,
              step: 1
            }
          });
        }
      },
      onError: (error: any) => {
        showToaster({
          message: typeof (error as any)?.message === 'string' ? (error as any).message : 'Error in registering application.',
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          }
        });
      }
    });
  };

  // Step 5: Handle course validation logic after all data is loaded
  useEffect(() => {
    if (!dataLoadingComplete) return;
    if (!isInitialized) return;

    // Case 1: User has registration but URL courseId is different
    if (courseId?.length && registrationDetails?._id && courseId !== registrationDetails?.courseId && selectedCourseData?._id) {
      setShowValidateCourseModal(true);
    }
    // Case 2: User has fresh registration but without courseId in URL, so we need to show course selection modal to select a course
    else if (!courseId?.length && !registrationDetails?._id) {
      asyncLeadCapture();
      setShowCourseSelectionModal(true);
    }
    // Case 2: Fresh registration with courseId from URL but no registration details, automatically register user with that course
    else if (courseId?.length && !registrationDetails?._id && courses?.length) {
      const targetCourseData = courses?.find((course: any) => course?._id === courseId);
      if (!targetCourseData?._id) {
        showToaster({
          message: 'Invalid course selected. Please select a valid course.',
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          }
        });
        return;
      }
      setSelectedCourseData(targetCourseData);
      submitRegisterForm(targetCourseData);
    } else if (coming_from === 'admission-process') {
      // Case 3: User coming from admission process
      setShowEligibleAdmissionModal(true);
    }
    // Case 4: Everything is in sync i.e no conflicts in courseId
    else {
      asyncLeadCapture();
      setShowValidateCourseModal(false);
      router.replace({
        pathname: '/register',
        query: {
          courseId: registrationDetails?.courseId,
          step: registrationDetails?.step || 1
        }
      });
    }
    setIsInitialized(false);
  }, [dataLoadingComplete, courseId, registrationDetails?._id, registrationDetails?.courseId, isInitialized, selectedCourseData?._id]);
  const handleTrackShift = () => {
    trackShift(undefined, {
      onSuccess: (response: any) => {
        const isSuccess = response?.status === 200 || response?.statusCode === 200;
        const payload = response?.data;
        if (isSuccess && payload?.courseId) {
          setRegistrationDetails(payload);
          setShowTrackShiftModal(false);
          router.replace({
            pathname: '/register',
            query: {
              courseId: payload.courseId,
              step: payload.step || 1
            }
          });
        } else {
          showToaster({
            message: 'Unexpected response. Please try again.',
            variant: 'error',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center'
            }
          });
        }
      },
      onError: () => {
        // Error toast is handled by useTrackShift hook internally
        // Keep modal open so user can retry track-shift action
      }
    });
  };
  const handleContinue = () => {
    setShowValidateCourseModal(false);
    setIsInitialized(false);
    router.push({
      pathname: '/register',
      query: {
        courseId: registrationDetails?.courseId,
        step: registrationDetails?.step || 1
      }
    });
  };
  const handleSwitchProgram = (newCourse: any) => {
    setSelectedCourseData(newCourse);
    setDataLoadingComplete(false);
    setShowValidateCourseModal(false);
    submitRegisterForm(newCourse);
  };
  const handleCourseSelection = (data: any) => {
    if (data?._id) {
      setSelectedCourseData(data);
      setDataLoadingComplete(false);
      setShowCourseSelectionModal(false);
      submitRegisterForm(data);
    }
  };
  return <>
      {!dataLoadingComplete || isUpdatingRegistrationDetails || showValidateCourseModal ? <div className="flex items-center justify-center h-full">
          <Loader message="Loading..." />
        </div> : <div className="flex-grow overflow-hidden scroll-smooth">
          <main className="w-full h-full sm:!py-6">{children}</main>
        </div>}

      <ValidateCourseModal isOpen={showValidateCourseModal} applicationFeePaidStatus={registrationDetails?.isPaid ?? false} onClose={() => setShowValidateCourseModal(false)} onContinue={handleContinue} onSwitchProgram={handleSwitchProgram} data-sentry-element="ValidateCourseModal" data-sentry-source-file="InitializeComponent.tsx" />
      {showCourseSelectionModal ? <CourseSelection isOpen={showCourseSelectionModal} onClose={handleCourseSelection} closeOnOutsideClick={false} /> : <></>}

      {showEligibleAdmissionModal && <EligibleForAdmissionModal isOpen onClose={() => setShowEligibleAdmissionModal(false)} />}

      {showTrackShiftModal && <TrackShiftModal isOpen={showTrackShiftModal} isLoading={isTrackShiftLoading} onContinueWithCEE={handleTrackShift} />}
    </>;
};
export default InitializeComponent;